import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Academy,
  Coupon,
  TierFeatureKey,
  type MarketFeatureFlagState,
  type Organization,
  isStripeConnectCountrySupported,
  resolveAcademyStripeConnectCountry,
} from '@court-sync/shared';
import { FeatureGate } from '@/components/FeatureGate';
import { academyService } from '../../../../services/academy';
import { Button, Card, CardContent, Input, Field, FieldLabel, Switch } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import type { OnboardingEmbeddedPageProps } from '@/components/onboarding/onboardingEmbeddedPage';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import {
  ConnectComponentsProvider,
  ConnectPayments,
  ConnectPayouts,
  ConnectNotificationBanner,
} from '@stripe/react-connect-js';
import {
  getAccountStatus,
  createAccountSession,
  AccountStatusResponse,
} from '../../../../services/connect';
import { STRIPE_PUBLISHABLE_KEY } from '../../../../config/api';
import {
  CreditCard,
  TrendingUp,
  Loader2,
  Plus,
  Tag,
} from 'lucide-react';
import { useAuthStore } from '../../../../store/auth';
import { useCurrentAcademy, getUserAcademiesFromCache } from '../../../../hooks/useDataStore';
import toast from 'react-hot-toast';
import { RiskStatusCard } from '../../../../components/RiskStatusCard';
import { useResolvedThemeIsDark } from '../../../../store/theme';
import {
  applyStripeConnectAppearance,
  getStripeConnectAppearanceVariables,
} from '../../../../utils/stripeConnectAppearance';
import { AccountingIntegrationsSection } from './AccountingIntegrationsSection';
import PaymentsSettingsTab from './PaymentsSettingsTab';
import PaymentsTaxTab from './PaymentsTaxTab';
import { ClubFeatureGate } from '@/components/club/ClubFeatureGate';
import { MembershipDiscountsSection } from '@/components/club/MembershipDiscountsSection';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';
import { useClubOwnerAccess } from '@/hooks/useClubOwnerAccess';
import { useMatchPlayAccess } from '@/hooks/useMatchPlayAccess';
import { organizationService } from '@/services/organization';
import {
  PaymentsConnectEmptyState,
  resolvePaymentsConnectEmptyStateVariant,
} from './PaymentsConnectEmptyState';
import { StudentBillingReport } from '@/components/students/StudentBillingReport';
import { getCurrencyFromRegion } from '@/utils/currency';
import {
  useFeatureAccess,
  useManagementDashboardFeatureAccess,
} from '../../../../hooks/useFeatureAccess';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import {
  DashboardDataCard,
  DashboardFilterCard,
  dashboardTableCellClass,
  dashboardTableDataRowClass,
  dashboardTableHeadClass,
  dashboardTableHeaderRowClass,
} from '@/components/dashboard/DashboardSectionCard';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { getFeatureFlags } from '../../../../services/featureFlags';
import { PAYMENTS_MANAGEMENT_TOUR_STEP_IDS } from '@/lib/tour-constants';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { useAutoPageTour } from '@/hooks/useAutoPageTour';
import type { PageTourKey } from '@/lib/tours/registry';
import { TierUnlockBadge } from '@/components/tier/TierUnlockBadge';
import { getPaymentsTabTierUnlockLevel } from '@/lib/tierUnlockBadges';
import {
  getPaymentsTabHelp,
  PAYMENTS_STARTER_TOUR_KEY,
  resolvePaymentsAutoTourKey,
  type PaymentsTourTab,
  schedulePaymentsSettingsTourStart,
  schedulePaymentsStarterTourStart,
} from '@/lib/tours/paymentsManagementTour';

type PaymentsMainTab =
  | 'student-billing'
  | 'dashboard'
  | 'tax'
  | 'settings'
  | 'discounts'
  | 'promo-codes'
  | 'accounting';

const STRIPE_CONNECT_PAYMENTS_TABS: { id: PaymentsMainTab; label: string; mobileLabel?: string }[] = [
  { id: 'dashboard', label: 'Payments Report' },
  { id: 'settings', label: 'Settings' },
  { id: 'tax', label: 'Tax' },
  { id: 'discounts', label: 'Discounts' },
  { id: 'promo-codes', label: 'Promo codes', mobileLabel: 'Promo' },
  { id: 'accounting', label: 'Accounting Integration', mobileLabel: 'Accounting' },
];

const PAYMENT_PROCESSING_TABS = new Set<PaymentsMainTab>([
  'dashboard',
  'tax',
  'discounts',
  'promo-codes',
]);

const BILLING_REPORT_TAB: { id: PaymentsMainTab; label: string; mobileLabel?: string } = {
  id: 'student-billing',
  label: 'Billing Report',
  mobileLabel: 'Billing',
};

export default function PaymentManagement({
  embedded = false,
  defaultTab,
}: OnboardingEmbeddedPageProps = {}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { startPageTour, startTourWithCompletionKey } = usePageTourActions();
  const { currentClub, setCurrentClub } = useCurrentClub();
  const { hasClubManagement } = useClubModuleAccess();
  const { canManageClubPayments } = useClubOwnerAccess();
  const { loadUserAcademies, loadCurrentAcademy } = useCurrentAcademy();
  const isDark = useResolvedThemeIsDark();
  // Stripe Connect state
  const [connectAccountId, setConnectAccountId] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<AccountStatusResponse | null>(null);
  const [stripeConnectInstance, setStripeConnectInstance] = useState<any>(null);
  const [taxConnectInstance, setTaxConnectInstance] = useState<any>(null);
  const [activeConnectTab, setActiveConnectTab] = useState<'payments' | 'payouts'>('payments');
  const [, setInitializingConnect] = useState(false);

  const { hasAccess: hasPaymentAccess } = useManagementDashboardFeatureAccess(
    TierFeatureKey.PAYMENT_PROCESSING
  );
  const { hasAccess: hasAccountingAccess } = useFeatureAccess(TierFeatureKey.ACCOUNTING_INTEGRATIONS);
  const { hasAccess: hasMatchPlay } = useMatchPlayAccess();

  const tabParam = searchParams.get('tab') as PaymentsMainTab | null;

  const setMainTab = useCallback(
    (tab: PaymentsMainTab) => {
      setSearchParams({ tab }, { replace: true });
    },
    [setSearchParams]
  );

  // Academy with payment processing (for discounts & promo codes)
  const [paymentAcademy, setPaymentAcademy] = useState<Academy | null>(null);
  const [, setAcademyForPayments] = useState<Academy | null>(null);
  const [siblingSaving, setSiblingSaving] = useState(false);
  const [siblingEnabled, setSiblingEnabled] = useState(false);
  const [siblingPercent, setSiblingPercent] = useState(10);
  const [siblingMinChildren, setSiblingMinChildren] = useState(2);
  const [siblingScope, setSiblingScope] = useState<'same_term' | 'same_academy'>('same_term');

  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponSubmitting, setCouponSubmitting] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: '',
    type: 'percent' as 'percent' | 'fixed',
    value: '',
    validFrom: '',
    validUntil: '',
    maxRedemptions: '1',
  });

  /** True until initial Connect / academy fetch finishes (prevents flashing the “enable Stripe” empty state). */
  const [connectAccountLoading, setConnectAccountLoading] = useState(true);
  const [marketFeatureFlags, setMarketFeatureFlags] = useState<MarketFeatureFlagState>({});
  const [paymentOrganization, setPaymentOrganization] = useState<Organization | null>(null);
  const academyCurrency = getCurrencyFromRegion(
    paymentAcademy?.region ?? currentClub?.region ?? paymentOrganization?.region
  );

  useEffect(() => {
    if (!user || !canManageClubPayments) {
      navigate('/dashboard');
      return;
    }

    loadConnectAccount();
    getFeatureFlags().then(setMarketFeatureFlags).catch(() => undefined);
  }, [user, canManageClubPayments, navigate, currentClub?.id]);

  /**
   * Load Stripe Connect account information
   */
  const loadConnectAccount = async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setConnectAccountLoading(true);
    }
    try {
      await loadUserAcademies({ force: true });
      const academies = getUserAcademiesFromCache();
      let resolvedConnectAccountId: string | null = null;

      if (academies.length > 0) {
        const academy = academies.find((a: Academy) => a.stripeConnectAccountId) || academies[0];
        setPaymentAcademy(academy);
        resolvedConnectAccountId = academy?.stripeConnectAccountId ?? null;
      } else {
        setPaymentAcademy(null);
        const orgRes = await organizationService.getMyOrganization();
        const organization = orgRes.success ? orgRes.data : null;
        setPaymentOrganization(organization ?? null);
        resolvedConnectAccountId =
          currentClub?.stripeConnectAccountId ?? organization?.stripeConnectAccountId ?? null;
      }

      if (resolvedConnectAccountId) {
        setConnectAccountId(resolvedConnectAccountId);
        const status = await getAccountStatus(resolvedConnectAccountId);
        setAccountStatus(status);
        if (hasPaymentAccess && (status.chargesEnabled || status.payoutsEnabled)) {
          initializeStripeConnect(resolvedConnectAccountId);
        }
      } else {
        setConnectAccountId(null);
        setAccountStatus(null);
        setStripeConnectInstance(null);
      }
    } catch (error: any) {
      console.error('Error loading Connect account:', error);
      // Non-critical, continue with regular payment management
    } finally {
      if (!options?.silent) {
        setConnectAccountLoading(false);
      }
    }
  };

  // Load full academy for discounts when payment processing is enabled
  const paymentProcessingEnabled = Boolean(connectAccountId && accountStatus?.chargesEnabled);
  const clubPaymentsEnabled = hasClubManagement && canManageClubPayments && !paymentAcademy;
  const paymentProcessingAllowed = hasPaymentAccess || clubPaymentsEnabled;
  const showManualBillingTab =
    !connectAccountId &&
    Boolean(
      paymentAcademy ||
        (hasClubManagement && currentClub && hasMatchPlay)
    );
  const billingOrganizationId =
    paymentAcademy?.organizationId ?? paymentOrganization?.id ?? currentClub?.organizationId;
  const billingContext =
    paymentAcademy && hasClubManagement && currentClub
      ? 'combined'
      : paymentAcademy
        ? 'academy_only'
        : 'club_only';
  const canShowPaymentsHub = Boolean(
    connectAccountId || paymentAcademy || paymentOrganization || (hasClubManagement && currentClub)
  );

  const paymentsTabs = useMemo(() => {
    let tabs = showManualBillingTab
      ? (() => {
          const [paymentsReport, ...rest] = STRIPE_CONNECT_PAYMENTS_TABS;
          return [paymentsReport, BILLING_REPORT_TAB, ...rest];
        })()
      : [...STRIPE_CONNECT_PAYMENTS_TABS];

    if (!paymentProcessingAllowed) {
      tabs = tabs.filter((tab) => !PAYMENT_PROCESSING_TABS.has(tab.id));
    }
    if (!hasAccountingAccess) {
      tabs = tabs.filter((tab) => tab.id !== 'accounting');
    }

    return tabs;
  }, [showManualBillingTab, paymentProcessingAllowed, hasAccountingAccess]);

  const validTabs = useMemo(() => new Set(paymentsTabs.map((t) => t.id)), [paymentsTabs]);

  const academyConnectCountry = paymentAcademy
    ? resolveAcademyStripeConnectCountry(paymentAcademy)
    : null;
  const lessonConnectSupported =
    academyConnectCountry === null ||
    isStripeConnectCountrySupported(academyConnectCountry, marketFeatureFlags);

  const connectTabBlocked =
    Boolean(paymentAcademy) && !lessonConnectSupported;

  const defaultMainTab: PaymentsMainTab = connectTabBlocked
    ? 'settings'
    : showManualBillingTab
      ? 'student-billing'
      : paymentProcessingEnabled
        ? 'dashboard'
        : 'settings';

  const mainTab: PaymentsMainTab =
    tabParam && validTabs.has(tabParam) ? tabParam : defaultMainTab;

  const isPaymentsTabDisabled = useCallback(
    (tabId: PaymentsMainTab): boolean => {
      if (tabId === 'student-billing') return false;
      if (connectTabBlocked) return tabId !== 'settings';
      if (
        tabId === 'dashboard' ||
        tabId === 'discounts' ||
        tabId === 'promo-codes'
      ) {
        return !paymentProcessingEnabled;
      }
      if (tabId === 'tax' || tabId === 'accounting') {
        return !connectAccountId;
      }
      return false;
    },
    [connectTabBlocked, connectAccountId, paymentProcessingEnabled]
  );

  const visiblePaymentsTabs = useMemo(
    () => paymentsTabs.filter((tab) => !isPaymentsTabDisabled(tab.id)),
    [paymentsTabs, isPaymentsTabDisabled]
  );

  const showPaymentsTabNav = visiblePaymentsTabs.length > 1;

  const resolvedMainTab: PaymentsMainTab = visiblePaymentsTabs.some((tab) => tab.id === mainTab)
    ? mainTab
    : (visiblePaymentsTabs[0]?.id ?? defaultMainTab);

  useEffect(() => {
    if (connectAccountLoading) return;
    if (!tabParam || !validTabs.has(tabParam) || tabParam !== resolvedMainTab) {
      setSearchParams({ tab: resolvedMainTab }, { replace: true });
    }
  }, [connectAccountLoading, tabParam, validTabs, resolvedMainTab, setSearchParams]);

  const setupFocusMode =
    !showPaymentsTabNav &&
    resolvedMainTab === 'settings' &&
    !paymentProcessingEnabled &&
    (paymentProcessingAllowed || connectTabBlocked || !lessonConnectSupported);

  const connectEmptyStateVariant = resolvePaymentsConnectEmptyStateVariant(
    hasPaymentAccess,
    connectAccountId,
    lessonConnectSupported,
    { clubPaymentsEnabled }
  );

  useEffect(() => {
    if (!tabParam) return;
    if (!hasAccountingAccess && tabParam === 'accounting') {
      setMainTab('settings');
      return;
    }
    if (!paymentProcessingAllowed && PAYMENT_PROCESSING_TABS.has(tabParam)) {
      setMainTab(showManualBillingTab ? 'student-billing' : 'settings');
    }
  }, [hasAccountingAccess, paymentProcessingAllowed, showManualBillingTab, tabParam, setMainTab]);

  useEffect(() => {
    if (!embedded || !defaultTab) return;
    if (validTabs.has(defaultTab as PaymentsMainTab)) {
      setMainTab(defaultTab as PaymentsMainTab);
    }
  }, [embedded, defaultTab, validTabs, setMainTab]);

  useEffect(() => {
    if (!connectTabBlocked || !tabParam) return;
    if (tabParam !== 'settings') {
      setMainTab('settings');
    }
  }, [connectTabBlocked, tabParam, setMainTab]);

  useEffect(() => {
    if (connectAccountLoading) return;
    if (tabParam && isPaymentsTabDisabled(tabParam)) {
      setMainTab(resolvedMainTab);
    }
  }, [
    connectAccountLoading,
    tabParam,
    isPaymentsTabDisabled,
    resolvedMainTab,
    setMainTab,
  ]);

  useEffect(() => {
    if (connectAccountLoading) return;
    if (!connectAccountId && tabParam === 'student-billing') return;
    if (connectAccountId && mainTab === 'student-billing') {
      setMainTab(paymentProcessingEnabled ? 'dashboard' : 'settings');
    }
  }, [
    connectAccountId,
    connectAccountLoading,
    mainTab,
    paymentProcessingEnabled,
    tabParam,
    setMainTab,
  ]);
  useEffect(() => {
    if (!paymentProcessingEnabled || !paymentAcademy?.id) return;
    academyService.getAcademy(paymentAcademy.id).then((res) => {
      if (res.data) {
        setAcademyForPayments(res.data);
        setSiblingEnabled(res.data.siblingDiscountEnabled ?? false);
        setSiblingPercent(res.data.siblingDiscountPercent ?? 10);
        setSiblingMinChildren(res.data.siblingDiscountMinChildren ?? 2);
        setSiblingScope(res.data.siblingDiscountScope || 'same_term');
      }
    });
  }, [paymentProcessingEnabled, paymentAcademy?.id]);

  const loadCoupons = () => {
    if (!paymentAcademy?.id) return;
    setCouponsLoading(true);
    academyService
      .getAcademyCoupons(paymentAcademy.id)
      .then((res) => setCoupons(res.data || []))
      .catch(() => {
        toast.error('Failed to load promo codes');
        setCoupons([]);
      })
      .finally(() => setCouponsLoading(false));
  };
  useEffect(() => {
    if (paymentAcademy?.id && paymentProcessingEnabled) loadCoupons();
  }, [paymentAcademy?.id, paymentProcessingEnabled]);

  const openCouponForm = () => {
    setShowCouponForm(true);
    setCouponForm({
      code: '',
      type: 'percent',
      value: '',
      validFrom: '',
      validUntil: '',
      maxRedemptions: '1',
    });
  };

  const submitCouponForm = async () => {
    if (!paymentAcademy?.id) return;
    if (!couponForm.code.trim()) {
      toast.error('Code is required');
      return;
    }
    const valueNum =
      couponForm.type === 'percent' ? Number(couponForm.value) : Math.round(Number(couponForm.value) * 100);
    if (Number.isNaN(valueNum) || valueNum < 0) {
      toast.error('Invalid value');
      return;
    }
    if (couponForm.type === 'percent' && valueNum > 100) {
      toast.error('Percent cannot exceed 100');
      return;
    }
    setCouponSubmitting(true);
    try {
      await academyService.createAcademyCoupon(paymentAcademy.id, {
        code: couponForm.code.trim(),
        type: couponForm.type,
        value:
          couponForm.type === 'percent'
            ? Number(couponForm.value)
            : Math.round(Number(couponForm.value) * 100),
        validFrom: couponForm.validFrom || undefined,
        validUntil: couponForm.validUntil || undefined,
        maxRedemptions: couponForm.maxRedemptions ? Number(couponForm.maxRedemptions) : 1,
      });
      toast.success('Promo code created');
      setShowCouponForm(false);
      setCouponForm({
        code: '',
        type: 'percent',
        value: '',
        validFrom: '',
        validUntil: '',
        maxRedemptions: '1',
      });
      loadCoupons();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to create');
    } finally {
      setCouponSubmitting(false);
    }
  };

  const saveSiblingDiscount = async () => {
    if (!paymentAcademy?.id) return;
    setSiblingSaving(true);
    try {
      await academyService.updateAcademy(paymentAcademy.id, {
        siblingDiscountEnabled: siblingEnabled,
        siblingDiscountPercent: siblingPercent,
        siblingDiscountMinChildren: siblingMinChildren,
        siblingDiscountScope: siblingScope,
      });
      toast.success('Sibling discount updated');
      const res = await academyService.getAcademy(paymentAcademy.id);
      if (res.data) setAcademyForPayments(res.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to update');
    } finally {
      setSiblingSaving(false);
    }
  };

  /**
   * Initialize Stripe Connect instance for embedded dashboard components
   */
  const initializeStripeConnect = async (accountId: string) => {
    try {
      setInitializingConnect(true);
      const publishableKey = STRIPE_PUBLISHABLE_KEY;
      if (!publishableKey) {
        throw new Error('Stripe publishable key not configured');
      }

      const instance = await loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret: async () => {
          try {
            const response = await createAccountSession(accountId, {
              payments: {
                enabled: true,
              },
              payouts: { enabled: true },
              payment_details: {
                enabled: true,
                features: {
                  refund_management: true,
                  dispute_management: true,
                  capture_payments: true,
                },
              },
            });
            return response.clientSecret;
          } catch (error: any) {
            console.error('Error fetching client secret:', error);
            toast.error('Failed to initialize Stripe Connect');
            return undefined;
          }
        },
        appearance: {
          variables: getStripeConnectAppearanceVariables(isDark),
        },
      });

      setStripeConnectInstance(instance);
    } catch (error: any) {
      console.error('Error initializing Stripe Connect:', error);
      toast.error('Failed to initialize Stripe Connect');
    } finally {
      setInitializingConnect(false);
    }
  };

  const initializeTaxConnect = async (accountId: string) => {
    if (taxConnectInstance) {
      return;
    }
    try {
      const publishableKey = STRIPE_PUBLISHABLE_KEY;
      if (!publishableKey) {
        throw new Error('Stripe publishable key not configured');
      }

      const instance = await loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret: async () => {
          const response = await createAccountSession(accountId, {
            tax_settings: { enabled: true },
            tax_registrations: { enabled: true },
          });
          return response.clientSecret;
        },
        appearance: {
          variables: getStripeConnectAppearanceVariables(isDark),
        },
      });

      setTaxConnectInstance(instance);
    } catch (error: any) {
      console.error('Error initializing Stripe Tax Connect:', error);
      toast.error('Failed to initialize Stripe Tax');
    }
  };

  useEffect(() => {
    applyStripeConnectAppearance(stripeConnectInstance, isDark);
  }, [isDark, stripeConnectInstance]);

  useEffect(() => {
    applyStripeConnectAppearance(taxConnectInstance, isDark);
  }, [isDark, taxConnectInstance]);

  useEffect(() => {
    setTaxConnectInstance(null);
  }, [connectAccountId]);

  useEffect(() => {
    if (connectAccountId && resolvedMainTab === 'tax' && !taxConnectInstance) {
      initializeTaxConnect(connectAccountId);
    }
  }, [connectAccountId, resolvedMainTab, taxConnectInstance]);

  const reloadAcademyData = useCallback(async () => {
    if (!paymentAcademy?.id) return;
    await loadCurrentAcademy(paymentAcademy.id, { force: true });
    const res = await academyService.getAcademy(paymentAcademy.id);
    if (res.data) {
      setPaymentAcademy(res.data);
    }
  }, [paymentAcademy?.id, loadCurrentAcademy]);

  const reloadAcademy = async () => {
    await reloadAcademyData();
    await loadConnectAccount({ silent: true });
  };

  const activePaymentsTourKey = resolvePaymentsAutoTourKey(
    resolvedMainTab as PaymentsTourTab,
    paymentProcessingAllowed,
  );

  const manualCollectionContext =
    clubPaymentsEnabled || (hasClubManagement && billingContext !== 'academy_only')
      ? 'club_before_connect'
      : 'academy_starter';

  const shouldUseManualCollectionTour =
    !paymentProcessingAllowed ||
    (showManualBillingTab && !connectAccountId && manualCollectionContext === 'club_before_connect');

  const launchPaymentsStarterTour = useCallback(() => {
    schedulePaymentsStarterTourStart(startTourWithCompletionKey, {
      onEnterBillingReport: () => setMainTab('student-billing'),
      onEnterSettings: () => setMainTab('settings'),
      manualCollectionContext,
    });
  }, [manualCollectionContext, setMainTab, startTourWithCompletionKey]);

  const launchPaymentsSettingsTour = useCallback(() => {
    if (shouldUseManualCollectionTour) {
      launchPaymentsStarterTour();
      return;
    }
    schedulePaymentsSettingsTourStart(startTourWithCompletionKey, {
      isStarterPlan: false,
    });
  }, [launchPaymentsStarterTour, shouldUseManualCollectionTour, startTourWithCompletionKey]);

  const startPaymentsTabTour = useCallback(
    (tourKey: PageTourKey) => {
      if (
        shouldUseManualCollectionTour &&
        (tourKey === 'academy-payments-billing-report' ||
          tourKey === 'academy-payments-settings' ||
          tourKey === PAYMENTS_STARTER_TOUR_KEY)
      ) {
        launchPaymentsStarterTour();
        return;
      }
      if (tourKey === 'academy-payments-settings') {
        launchPaymentsSettingsTour();
        return;
      }
      startPageTour(tourKey, { skipWelcomeDialog: true });
    },
    [
      launchPaymentsSettingsTour,
      launchPaymentsStarterTour,
      shouldUseManualCollectionTour,
      startPageTour,
    ],
  );

  const isPaymentsTabTourReady = useMemo(() => {
    if (connectAccountLoading || !user) return false;
    switch (resolvedMainTab) {
      case 'dashboard':
        return Boolean(connectAccountId && stripeConnectInstance);
      case 'student-billing':
        return Boolean(billingOrganizationId);
      default:
        return true;
    }
  }, [
    billingOrganizationId,
    connectAccountId,
    connectAccountLoading,
    resolvedMainTab,
    stripeConnectInstance,
    user,
  ]);

  useAutoPageTour(activePaymentsTourKey, isPaymentsTabTourReady, {
    onStart: startPaymentsTabTour,
  });

  const paymentsTabHelp = useMemo(
    () => getPaymentsTabHelp(resolvedMainTab as PaymentsTourTab),
    [resolvedMainTab],
  );

  const paymentsTabHelpTour = useMemo(() => {
    if (!activePaymentsTourKey || !paymentsTabHelp.tourLabel) {
      return [];
    }
    return [
      {
        tourKey: activePaymentsTourKey,
        label: paymentsTabHelp.tourLabel,
        description: paymentsTabHelp.tourDescription,
        onStart:
          shouldUseManualCollectionTour || resolvedMainTab === 'settings'
            ? launchPaymentsSettingsTour
            : undefined,
      },
    ];
  }, [
    activePaymentsTourKey,
    launchPaymentsSettingsTour,
    paymentsTabHelp.tourDescription,
    paymentsTabHelp.tourLabel,
    resolvedMainTab,
    shouldUseManualCollectionTour,
  ]);

  if (!user || !canManageClubPayments) {
    return null;
  }

  return (
    <div className={embedded ? 'min-h-0' : 'min-h-screen'}>
      <div className="contents">
        {!embedded ? (
          <PageHeader
            title="Payments"
            help={
              <PageFeatureHelp title={paymentsTabHelp.title} tours={paymentsTabHelpTour}>
                <p>{paymentsTabHelp.description}</p>
              </PageFeatureHelp>
            }
            helpAnchorId={PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.TABS}
          />
        ) : null}

        {connectAccountLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-hidden />
          </div>
        ) : canShowPaymentsHub ? (
          <>
                {showPaymentsTabNav ? (
                  <div id={PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.TABS}>
                    <DashboardTabNav<PaymentsMainTab>
                      role="tablist"
                      tabs={visiblePaymentsTabs.map((tab) => ({
                        ...tab,
                        tabId: `payments-tab-${tab.id}`,
                        suffix: (
                          <TierUnlockBadge unlockTier={getPaymentsTabTierUnlockLevel(tab.id)} />
                        ),
                      }))}
                      activeTab={resolvedMainTab}
                      onChange={setMainTab}
                      ariaLabel="Payment settings"
                    />
                  </div>
                ) : null}

                {connectAccountId && lessonConnectSupported && paymentAcademy?.id ? (
                  <RiskStatusCard className="mb-6" enabled={Boolean(paymentAcademy?.id)} />
                ) : null}

                <>
                    {resolvedMainTab === 'student-billing' && billingOrganizationId && (
                      <div id={PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.BILLING_REPORT}>
                        <StudentBillingReport
                          organizationId={billingOrganizationId}
                          academyId={paymentAcademy?.id}
                          clubId={currentClub?.id}
                          currency={academyCurrency}
                          billingContext={billingContext}
                        />
                      </div>
                    )}

                    {resolvedMainTab === 'settings' && (
                <>
                <div id={PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.SETTINGS}>
                <PaymentsSettingsTab
                  academy={paymentAcademy}
                  organization={paymentOrganization}
                  organizationName={currentClub?.name ?? paymentOrganization?.name}
                  organizationRegion={currentClub?.region ?? paymentOrganization?.region ?? null}
                  allowClubConnect={hasClubManagement && canManageClubPayments}
                  club={hasClubManagement ? currentClub : null}
                  lessonConnectSupported={lessonConnectSupported}
                  setupFocusMode={setupFocusMode}
                  onAcademyUpdated={reloadAcademy}
                  onClubUpdated={setCurrentClub}
                  onConnectAccountUpdated={() => loadConnectAccount({ silent: true })}
                  stripeConnectInstance={stripeConnectInstance}
                />
                </div>
                </>
              )}

              {resolvedMainTab === 'tax' && connectAccountId && (
                <div id={PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.TAX}>
                  <PaymentsTaxTab
                    academy={paymentAcademy}
                    organization={paymentOrganization}
                    connectAccountId={connectAccountId}
                    stripeConnectInstance={taxConnectInstance}
                    onAcademyUpdated={reloadAcademy}
                  />
                </div>
              )}

              {/* Dashboard tab: Stripe Connect Payments & Payouts */}
              {resolvedMainTab === 'dashboard' && paymentProcessingEnabled && connectAccountId && (
                <div id={PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.DASHBOARD} className="space-y-4">
                  {!stripeConnectInstance ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                      <span className="ml-3 text-gray-600 dark:text-gray-400">Initializing Stripe Connect...</span>
                    </div>
                  ) : (
                    <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                      <div className="mb-6">
                        <ConnectNotificationBanner />
                      </div>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Manage payments and payouts directly from your connected account
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant={activeConnectTab === 'payments' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setActiveConnectTab('payments')}
                          >
                            <CreditCard className="h-4 w-4 mr-2" />
                            Payments
                          </Button>
                          <Button
                            variant={activeConnectTab === 'payouts' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setActiveConnectTab('payouts')}
                            disabled={!accountStatus?.payoutsEnabled}
                          >
                            <TrendingUp className="h-4 w-4 mr-2" />
                            Payouts
                          </Button>
                        </div>
                      </div>
                      <div className="min-h-[600px] overflow-hidden">
                        {activeConnectTab === 'payments' ? (
                          <ConnectPayments />
                        ) : (
                          <ConnectPayouts />
                        )}
                      </div>
                    </ConnectComponentsProvider>
                  )}
                </div>
              )}

              {resolvedMainTab === 'accounting' && paymentAcademy && (
                <div id={PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.ACCOUNTING}>
                  <AccountingIntegrationsSection academy={paymentAcademy} />
                </div>
              )}

              {resolvedMainTab === 'discounts' && (
                <div id={PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.DISCOUNTS} className="space-y-6">
                  {currentClub && hasClubManagement ? (
                    <ClubFeatureGate feature={TierFeatureKey.MEMBERSHIPS} silent>
                      <MembershipDiscountsSection clubId={currentClub.id} />
                    </ClubFeatureGate>
                  ) : null}

                  {paymentProcessingEnabled && paymentAcademy ? (
                    <FeatureGate feature={TierFeatureKey.DISCOUNTS} inline>
                      <DashboardFilterCard
                        title="Sibling discount"
                        description="Offer a discount when the same parent books for multiple children (e.g. 10% off when 2+ children are enrolled)."
                      >
                        <div className="space-y-6">
                          <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-muted/30 px-4 py-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground">Enable sibling discount</p>
                              <p className="text-sm text-muted-foreground">
                                Automatically applied at checkout when eligibility rules match.
                              </p>
                            </div>
                            <Switch
                              checked={siblingEnabled}
                              onCheckedChange={setSiblingEnabled}
                              aria-label="Enable sibling discount"
                            />
                          </div>
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            <Field>
                              <FieldLabel htmlFor="siblingPercent">Discount %</FieldLabel>
                              <Input
                                id="siblingPercent"
                                type="number"
                                value={siblingPercent}
                                onChange={(e) => setSiblingPercent(Number(e.target.value))}
                                placeholder="10"
                                min={0}
                                max={100}
                                disabled={!siblingEnabled}
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="siblingMinChildren">Min. number of children</FieldLabel>
                              <Input
                                id="siblingMinChildren"
                                type="number"
                                value={siblingMinChildren}
                                onChange={(e) => setSiblingMinChildren(Number(e.target.value))}
                                placeholder="2"
                                min={2}
                                disabled={!siblingEnabled}
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="siblingScope">Scope</FieldLabel>
                              <select
                                id="siblingScope"
                                value={siblingScope}
                                onChange={(e) => setSiblingScope(e.target.value as 'same_term' | 'same_academy')}
                                className="input"
                                disabled={!siblingEnabled}
                              >
                                <option value="same_term">Same term</option>
                                <option value="same_academy">Same academy</option>
                              </select>
                              <p className="mt-1 text-xs text-muted-foreground">
                                Same term = enrolled in the same term; Same academy = any booking at this academy.
                              </p>
                            </Field>
                          </div>
                          <div className="flex justify-end border-t border-border pt-4">
                            <Button onClick={saveSiblingDiscount} disabled={siblingSaving || !siblingEnabled}>
                              {siblingSaving ? 'Saving...' : 'Save sibling discount'}
                            </Button>
                          </div>
                        </div>
                      </DashboardFilterCard>
                    </FeatureGate>
                  ) : null}
                </div>
              )}

              {resolvedMainTab === 'promo-codes' && paymentProcessingEnabled && paymentAcademy && (
                <FeatureGate feature={TierFeatureKey.COUPONS_PROMOS} inline>
                  <div id={PAYMENTS_MANAGEMENT_TOUR_STEP_IDS.PROMO_CODES} className="space-y-4">
                    {showCouponForm && (
                      <DashboardFilterCard
                        title="New promo code"
                        description="Codes apply to all lessons. Default is one redemption per user."
                      >
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Field>
                              <FieldLabel htmlFor="couponCode">Code</FieldLabel>
                              <Input
                                id="couponCode"
                                value={couponForm.code}
                                onChange={(e) => setCouponForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                                placeholder="e.g. SPRING10"
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="couponType">Discount type</FieldLabel>
                              <select
                                id="couponType"
                                value={couponForm.type}
                                onChange={(e) =>
                                  setCouponForm((f) => ({ ...f, type: e.target.value as 'percent' | 'fixed' }))
                                }
                                className="input"
                              >
                                <option value="percent">Percent off</option>
                                <option value="fixed">Fixed amount off</option>
                              </select>
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="couponValue">
                                {couponForm.type === 'percent' ? 'Percent (e.g. 10)' : 'Amount off (e.g. 5.00)'}
                              </FieldLabel>
                              <Input
                                id="couponValue"
                                type="number"
                                min={0}
                                step={couponForm.type === 'percent' ? 1 : 0.01}
                                value={couponForm.value}
                                onChange={(e) => setCouponForm((f) => ({ ...f, value: e.target.value }))}
                                placeholder={couponForm.type === 'percent' ? '10' : '5.00'}
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="couponMaxRedemptions">
                                Max redemptions per user (default 1)
                              </FieldLabel>
                              <Input
                                id="couponMaxRedemptions"
                                type="number"
                                min={1}
                                value={couponForm.maxRedemptions}
                                onChange={(e) => setCouponForm((f) => ({ ...f, maxRedemptions: e.target.value }))}
                                placeholder="1"
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="couponValidFrom">Valid from (optional)</FieldLabel>
                              <Input
                                id="couponValidFrom"
                                type="datetime-local"
                                value={couponForm.validFrom}
                                onChange={(e) => setCouponForm((f) => ({ ...f, validFrom: e.target.value }))}
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="couponValidUntil">Valid until (optional)</FieldLabel>
                              <Input
                                id="couponValidUntil"
                                type="datetime-local"
                                value={couponForm.validUntil}
                                onChange={(e) => setCouponForm((f) => ({ ...f, validUntil: e.target.value }))}
                              />
                            </Field>
                          </div>
                          <div className="flex justify-end gap-2 border-t border-border pt-4">
                            <Button type="button" variant="outline" onClick={() => setShowCouponForm(false)}>
                              Cancel
                            </Button>
                            <Button type="button" onClick={submitCouponForm} disabled={couponSubmitting}>
                              {couponSubmitting ? 'Creating...' : 'Create promo code'}
                            </Button>
                          </div>
                        </div>
                      </DashboardFilterCard>
                    )}

                    {couponsLoading ? (
                      <Card>
                        <CardContent className="flex justify-center py-12">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
                        </CardContent>
                      </Card>
                    ) : coupons.length === 0 && !showCouponForm ? (
                      <DashboardEmptyStateCard>
                        <DashboardEmptyStateIcon icon={Tag} />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          No promo codes yet
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                          Create promo codes for lesson payments. Students enter the code at checkout. Default is one
                          use per user per code.
                        </p>
                        <Button type="button" onClick={openCouponForm}>
                          <Plus className="h-4 w-4 mr-2 shrink-0" aria-hidden />
                          Create promo code
                        </Button>
                      </DashboardEmptyStateCard>
                    ) : coupons.length > 0 ? (
                      <DashboardDataCard
                        toolbar={
                          !showCouponForm ? (
                            <div className="flex justify-end">
                              <Button type="button" size="sm" onClick={openCouponForm}>
                                <Plus className="h-4 w-4 mr-2 shrink-0" aria-hidden />
                                Create promo code
                              </Button>
                            </div>
                          ) : null
                        }
                      >
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className={dashboardTableHeaderRowClass}>
                                <th className={dashboardTableHeadClass}>Code</th>
                                <th className={dashboardTableHeadClass}>Discount</th>
                                <th className={dashboardTableHeadClass}>Redemptions</th>
                                <th className={dashboardTableHeadClass}>Valid until</th>
                                <th className={dashboardTableHeadClass}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {coupons.map((c) => {
                                const discountLabel =
                                  c.type === 'percent' ? `${c.value}% off` : `$${(c.value / 100).toFixed(2)} off`;
                                const validUntilStr = c.validUntil
                                  ? new Date(c.validUntil).toLocaleDateString()
                                  : '—';
                                const isDeactivated = c.validUntil ? new Date(c.validUntil) < new Date() : false;
                                const maxPerUser = c.maxRedemptions ?? 1;
                                return (
                                  <tr key={c.id} className={dashboardTableDataRowClass}>
                                    <td className={`${dashboardTableCellClass} font-medium text-foreground`}>
                                      {c.code}
                                    </td>
                                    <td className={`${dashboardTableCellClass} text-muted-foreground`}>
                                      {discountLabel}
                                    </td>
                                    <td
                                      className={`${dashboardTableCellClass} text-muted-foreground`}
                                      title="Total uses so far; limit is per user"
                                    >
                                      {c.redemptionCount} use{c.redemptionCount !== 1 ? 's' : ''} · max {maxPerUser}{' '}
                                      per user
                                    </td>
                                    <td className={`${dashboardTableCellClass} text-muted-foreground`}>
                                      {validUntilStr}
                                    </td>
                                    <td className={dashboardTableCellClass}>
                                      {isDeactivated ? (
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={async () => {
                                            if (
                                              !confirm(
                                                'Reactivate this promo code? It will be valid for 1 year from now.'
                                              )
                                            )
                                              return;
                                            try {
                                              const oneYearFromNow = new Date();
                                              oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
                                              await academyService.updateAcademyCoupon(paymentAcademy.id, c.id, {
                                                validUntil: oneYearFromNow.toISOString(),
                                              });
                                              toast.success('Promo code reactivated');
                                              loadCoupons();
                                            } catch (err: any) {
                                              toast.error(err?.response?.data?.error || 'Failed to reactivate');
                                            }
                                          }}
                                        >
                                          Reactivate
                                        </Button>
                                      ) : (
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={async () => {
                                            if (!confirm('Deactivate this promo code by setting valid until to now?'))
                                              return;
                                            try {
                                              await academyService.updateAcademyCoupon(paymentAcademy.id, c.id, {
                                                validUntil: new Date().toISOString(),
                                              });
                                              toast.success('Promo code deactivated');
                                              loadCoupons();
                                            } catch (err: any) {
                                              toast.error(err?.response?.data?.error || 'Failed to deactivate');
                                            }
                                          }}
                                        >
                                          Deactivate
                                        </Button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </DashboardDataCard>
                    ) : null}
                  </div>
                </FeatureGate>
              )}
                </>
          </>
        ) : (
          <PaymentsConnectEmptyState
            variant={connectEmptyStateVariant}
            onGoToSettings={() => setMainTab('settings')}
          />
        )}
      </div>
    </div>
  );
}
